import axiosInstance from "@/lib/axiosInstance";

export const getEmailAiBaseUrl = (): string => {
  const configured = (import.meta.env.VITE_EMAIL_AI_API_URL as string | undefined)?.trim();
  const root = configured ? configured.replace(/\/+$/, "") : "/email-ai-api";
  return `${root}/api/internal`;
};

// The Email AI backend authenticates callers by verifying the user's Cognito id
// token (it reads the `email` claim to scope each user to their own inbox). The
// browser doesn't hold that token — it's kept server-side — so we fetch it from
// onengine-backend (authenticated by the ServEngine session cookie) and send it
// as the bearer for Email AI requests.
let idToken: string | null = null;
let pendingFetch: Promise<string> | null = null;
// Bumped on logout. A fetch already in flight cannot be cancelled, so its
// continuation checks this before caching: otherwise it would write the departing
// user's token back after the reset and authenticate the next user as them.
let sessionGeneration = 0;

const fetchIdToken = async (): Promise<string> => {
  const requestGeneration = sessionGeneration;
  const response = await axiosInstance.get("/session-management/id-token");
  const token = response.data?.data?.idToken as string | undefined;
  if (!token) {
    throw new Error("No Email AI id token available for the current session.");
  }
  if (requestGeneration === sessionGeneration) {
    idToken = token;
  }
  return token;
};

const loadIdToken = (): Promise<string> => {
  pendingFetch ??= fetchIdToken().finally(() => {
    pendingFetch = null;
  });
  return pendingFetch;
};

export const ensureEmailAiAccessToken = (): Promise<string> =>
  idToken ? Promise.resolve(idToken) : loadIdToken();

export const recoverEmailAiAccessToken = async (): Promise<string> => {
  idToken = null;
  return loadIdToken();
};

export const resetEmailAiAuth = (): void => {
  sessionGeneration += 1;
  idToken = null;
  pendingFetch = null;
};
